import { List } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../../../constants";
import { adminLogout } from "../../../services/logout/logout";
import LogoutConfirmDialog from "../../../components/common/logout_confirm_dialog";
import NavListItem from "./nav_list_item";
import useNavOptions from "./use_nav_options";

const DRAWER_SCROLL_KEY = "admin-drawer-scroll-top";

function matchesRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

export default function Drawer({
  horizontal = false,
  onItemClick,
}: {
  horizontal?: boolean;
  onItemClick?: () => void;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const listRef = useRef<HTMLUListElement | null>(null);
  const filteredOptions = useNavOptions();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // when a nav route is a sub-path of another (e.g. Manage Roles and Access
  // living under /clientMapping), only the most specific (longest) match
  // should render as active, otherwise both entries light up together.
  const activeRoute = filteredOptions
    ?.filter((option) => option.route !== "/logout" && matchesRoute(pathname, option.route))
    .sort((a, b) => b.route.length - a.route.length)[0]?.route;

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    const storedScrollTop = window.sessionStorage.getItem(DRAWER_SCROLL_KEY);
    if (storedScrollTop) {
      listElement.scrollTop = Number(storedScrollTop);
    }

    const activeItem = listElement.querySelector<HTMLElement>("[data-active='true']");
    activeItem?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [pathname, filteredOptions]);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    const handleScroll = () => {
      window.sessionStorage.setItem(
        DRAWER_SCROLL_KEY,
        String(listElement.scrollTop),
      );
    };

    listElement.addEventListener("scroll", handleScroll);

    return () => {
      listElement.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    // Must await before clearing tokens below - adminLogout()'s request
    // needs the current auth token to still be in storage when
    // HttpClient's interceptor attaches it (same order as the working
    // navbar logout in profile.tsx). Clearing it first/concurrently makes
    // the logout call go out unauthenticated, so it silently fails and
    // this never redirects.
    let redirected = false;
    try {
      const res = await adminLogout();
      if (res.status === 200 && res.data?.data?.redirectUrl) {
        redirected = true;
        window.location.href = res.data.data.redirectUrl;
      }
    } catch (error) {
      // Fall through - still clear local tokens below so the user isn't
      // stuck "logged in" on this device even if the backend call failed.
    } finally {
      localStorage.setItem(ACCESS_TOKEN, "");
      localStorage.setItem(REFRESH_TOKEN, "");
      sessionStorage.setItem(ACCESS_TOKEN, "");
      sessionStorage.setItem(REFRESH_TOKEN, "");
    }
    // Safety net: if the backend didn't hand back a redirect (call failed,
    // or returned no redirectUrl), still send the user to the login page
    // instead of leaving them on this now-unauthenticated page.
    if (!redirected) {
      navigate("/");
    }
    setIsLoggingOut(false);
    setLogoutDialogOpen(false);
  };

  return (
    <>
      <List
        ref={listRef}
        sx={{
          p: 1,
          width: "100%",
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {filteredOptions?.map((option) => (
          <NavListItem
            key={option.route}
            label={option.label}
            icon={option.icon}
            linkTo={option.route === "/logout" ? "" : option.route}
            active={option.route === activeRoute}
            horizontal={horizontal}
            onClick={
              option.route === "/logout"
                ? () => {
                  setLogoutDialogOpen(true);
                  onItemClick?.();
                }
                : onItemClick
            }
          />
        ))}
      </List>
      <LogoutConfirmDialog
        open={logoutDialogOpen}
        isLoggingOut={isLoggingOut}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
}
